import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { gql, useMutation } from '@apollo/client';
import Button from '../../../../../ui/canon/Button';

// RJ Seller Visibility Workflow — seller-side action bar for public :message
// conversations. Renders Invite to Meeting Room + Stop Messaging buttons.
// ("Stop Messaging" was named "End Responses" until 2026-09-24; the mutation and
// Ahoy events keep the old name on purpose — see rj_end_responses.)
// Also renders the NDA nudge modal when the parent signals it fired.
// Verbatim copy from Ashley — do NOT paraphrase. See:
// docs/plans/2026-07-17-001-feat-rj-seller-visibility-workflow-plan.md
const RJ_INVITE_TO_MEETING_ROOM = gql`
  mutation RjInviteToMeetingRoom($conversation_id: ID!, $deal_id: ID) {
    rj_invite_to_meeting_room(conversation_id: $conversation_id, deal_id: $deal_id) {
      success
      errors
      deal_interest_id
      deal_id
    }
  }
`;

const RJ_END_RESPONSES = gql`
  mutation RjEndResponses($conversation_id: ID!) {
    rj_end_responses(conversation_id: $conversation_id) {
      success
      errors
    }
  }
`;

// Added 2026-07-20 per Ashley's review — un-end path for End Responses.
const RJ_REOPEN_RESPONSES = gql`
  mutation RjReopenResponses($conversation_id: ID!) {
    rj_reopen_responses(conversation_id: $conversation_id) {
      success
      errors
    }
  }
`;

// Ashley's (RJ) nudge copy. Missing "with" corrected per her 2026-07-20 review
// comment on the client-friendly Notion doc.
//
// Second sentence amended after the 2026-08-04 check-in, where the flow changed
// so that "Invite to Meeting Room" starts the normal meeting room process
// instead of jumping to the NDA. The original read "…you put an NDA in place.
// This will put them in a meeting room with you…", which now has the causality
// backwards: the invite is what opens the room, and the NDA follows from inside
// it. Reworded using the check-in's own phrasing ("the NDA can be sent at any
// point from within the room"). NEEDS ASHLEY'S CONFIRMATION — the rest of her
// wording is preserved verbatim.
const NDA_NUDGE_COPY =
  "It looks as though you have had frequent conversation with this buyer. We suggest that before you move too far ahead, you invite them to a meeting room with you. You can put an NDA in place at any point from within the room, to ensure that there is a level of confidentiality to your conversations.";

// The Succession create-a-Meeting-Room flow — the same destination
// SuccessionDealsHome's "Create {dealRoomName}" button uses. It MUST be this and
// not the legacy `/deal/create`: that one is a different wizard that publishes via
// `updateDeal`, so the invite would never complete. This route renders
// Succession/Deals/DealWizard.jsx, which publishes via `publishDeal` and carries
// `invite_buyer_id` through to the server.
const meetingRoomWizardHref = (buyerUserId) =>
  `/succession/deals/wizard${buyerUserId ? `?invite_buyer_id=${buyerUserId}` : ''}`;

const RjSellerConversationActions = ({
  conversationId,
  conversationEnded,
  ndaNudgeTriggered,
  onNudgeDismissed,
  onEnded,
  onReopened,
  onInvited,
  ndaWizardHref,
  inviteState,
  buyerUserId,
  conversationType,
}) => {
  // C6 — do not offer Stop Messaging in a deal-room context.
  //
  // Two conditions, per the 2026-09-24 requirement:
  //   1. the thread itself is a deal-room type. Today rj_visibility_seller_view
  //      (personal_conversation_type.rb) already requires `message?`, so this is
  //      defensive — but it is also the exact mismatch that let the button render
  //      on a marketplace_eoi thread and fail with "Not a personal conversation".
  //   2. the pair is already in a Meeting Room. This one is live: the bar used to
  //      render "In your Meeting Room — shortlisted" NEXT TO an End Responses
  //      button. The text stays; the button goes.
  //
  // Deliberately scoped to this button only. `Block` (ChatPanel.jsx) is NOT
  // gated — it is not behind a feature flag, so gating it would change messaging
  // behaviour on every tenant rather than just RJ.
  const DEAL_ROOM_TYPES = ['deal_interest', 'deal', 'deal_team'];
  const inDealRoomThread = DEAL_ROOM_TYPES.includes(conversationType);
  const pairInMeetingRoom = branch === 'already_in_room';
  const canStopMessaging = !inDealRoomThread && !pairInMeetingRoom;

  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [showNudge, setShowNudge] = useState(false);
  const [showRoomPicker, setShowRoomPicker] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  // The invite succeeds server-side well before the parent's refetch lands, and
  // the branch is server-computed, so without this the bar sits on the old
  // `can_invite` state and the seller sees their click do nothing. This is a
  // transient acknowledgement only — it never invents a branch. The server value
  // takes over the moment it arrives (see the effect below).
  const [justInvited, setJustInvited] = useState(false);
  const [rjInvite, { loading: inviting }] = useMutation(RJ_INVITE_TO_MEETING_ROOM);
  const [rjEnd, { loading: ending }] = useMutation(RJ_END_RESPONSES);
  const [rjReopen, { loading: reopening }] = useMutation(RJ_REOPEN_RESPONSES);

  // Parent flips ndaNudgeTriggered on the send response payload; sync to local
  // dismissible state so the modal shows once per parent-signal.
  React.useEffect(() => {
    if (ndaNudgeTriggered) setShowNudge(true);
  }, [ndaNudgeTriggered]);

  // Branch (a): no Meeting Room yet → send the seller to the create flow rather
  // than silently minting a bare Deal (which is what the server used to do, and
  // why orphan Deals piled up invisibly).
  const goToWizard = () => {
    if (typeof window !== 'undefined' && window.ahoy) {
      window.ahoy.track('rj_meeting_room_create_started', { conversation_id: conversationId });
    }
    window.location.href = meetingRoomWizardHref(buyerUserId);
  };

  // The error banner replaced four alert() calls. alert() was transient — the user
  // dismissed it — but this banner persists, and without clearing it a failure on
  // one conversation follows the user into every other thread they open.
  // Reported 2026-09-24: "Conversation is not ended" stayed visible across the inbox.
  useEffect(() => {
    setErrorMessage(null);
  }, [conversationId]);

  const handleInvite = async (chosenDealId) => {
    setErrorMessage(null);
    // The server is the authority, but when it has already told us there is no
    // room, don't round-trip just to be refused.
    if (inviteState?.branch === 'must_create') {
      goToWizard();
      return;
    }

    // Prefer an explicit choice, then the server-resolved unambiguous room. Only
    // omitted when the seller has several, in which case the server refuses with
    // `multiple_meeting_rooms` and we show the picker.
    const dealId = chosenDealId || inviteState?.deal_id || null;

    try {
      const result = await rjInvite({
        variables: { conversation_id: conversationId, deal_id: dealId },
      });
      const payload = result?.data?.rj_invite_to_meeting_room;

      if (payload?.success) {
        if (typeof window !== 'undefined' && window.ahoy) {
          window.ahoy.track('rj_meeting_room_invited', {
            conversation_id: conversationId,
            deal_id: payload.deal_id,
          });
        }
        setJustInvited(true);
        setShowNudge(false); // suppress nudge if seller just invited
        setShowRoomPicker(false);
        // Awaited so a refetch that rejects surfaces here rather than leaving the
        // bar stuck on the pending acknowledgement forever.
        try {
          await (onInvited && onInvited(payload));
        } catch (refetchErr) {
          console.error('[RJ InviteToMeetingRoom] refresh after invite failed:', refetchErr);
          setJustInvited(false);
          setErrorMessage('Invited, but the view could not refresh. Reload to see the change.');
        }
        if (ndaWizardHref) {
          window.location.href = ndaWizardHref.replace(':deal_id', payload.deal_id);
        }
        return;
      }

      // Branch on the machine-readable reason, never on human copy.
      switch (payload?.reason) {
        case 'no_meeting_room':
          goToWizard();
          break;
        case 'multiple_meeting_rooms':
          setShowRoomPicker(true);
          break;
        default:
          setErrorMessage((payload?.errors || ['Failed to invite.']).join(', '));
      }
    } catch (err) {
      console.error('[RJ InviteToMeetingRoom] failed:', err);
      setErrorMessage('Failed to invite. Please try again.');
    }
  };

  const handleEnd = async () => {
    setErrorMessage(null);
    try {
      const result = await rjEnd({ variables: { conversation_id: conversationId } });
      const payload = result?.data?.rj_end_responses;
      if (payload?.success) {
        if (typeof window !== 'undefined' && window.ahoy) {
          window.ahoy.track('rj_end_responses_clicked', { conversation_id: conversationId });
        }
        onEnded && onEnded();
        setShowEndConfirm(false);
      } else {
        setErrorMessage((payload?.errors || ['Failed to stop messaging.']).join(', '));
      }
    } catch (err) {
      console.error('[RJ EndResponses] failed:', err);
      setErrorMessage('Failed to stop messaging. Please try again.');
    }
  };

  const dismissNudge = () => {
    if (typeof window !== 'undefined' && window.ahoy) {
      window.ahoy.track('rj_response_nudge_dismissed', { conversation_id: conversationId });
    }
    setShowNudge(false);
    onNudgeDismissed && onNudgeDismissed();
  };

  const handleReopen = async () => {
    setErrorMessage(null);
    try {
      const result = await rjReopen({ variables: { conversation_id: conversationId } });
      const payload = result?.data?.rj_reopen_responses;
      if (payload?.success) {
        if (typeof window !== 'undefined' && window.ahoy) {
          window.ahoy.track('rj_reopen_responses_clicked', { conversation_id: conversationId });
        }
        onReopened && onReopened();
      } else {
        setErrorMessage((payload?.errors || ['Failed to reopen.']).join(', '));
      }
    } catch (err) {
      console.error('[RJ ReopenResponses] failed:', err);
      setErrorMessage('Failed to reopen conversation. Please try again.');
    }
  };

  // Server-provided branch is the ONLY source of truth here. Defaulting to
  // `can_invite` keeps the button present when the field is absent (older cached
  // query, mid-refetch) rather than silently hiding the seller's only action —
  // the server still refuses safely and we route from the reason code.
  const branch = inviteState?.branch || 'can_invite';
  const rooms = inviteState?.deals || [];

  // Hand control back to the server the moment it agrees the buyer is in the
  // room, so the acknowledgement below can never outlive the real state.
  useEffect(() => {
    if (branch === 'already_in_room' && justInvited) setJustInvited(false);
  }, [branch, justInvited]);

  // Suppress the CTA while the invite is in flight or awaiting the refetch —
  // otherwise the seller can click it again and fire a second mutation.
  const showInviteButton =
    (branch === 'can_invite' || branch === 'must_create') && !justInvited;

  return (
    <>
      {errorMessage && (
        <div
          className="px-4 py-2 text-sm text-red-700 bg-red-50 border-t border-red-200"
          role="alert"
        >
          {errorMessage}
        </div>
      )}

      <div className="flex items-center gap-2 px-4 py-2 border-t border-succession-border bg-succession-bg-secondary">
        {/* Branch (c): already in the room — no invite CTA, just the state.
            The matrix suppresses both actions once the buyer is a deal interest. */}
        {branch === 'already_in_room' && (
          <span className="text-sm text-succession-text-secondary" data-rj-branch="already_in_room">
            In your Meeting Room
            {inviteState?.workflow_state ? ` — ${inviteState.workflow_state.replace(/_/g, ' ')}` : ''}
          </span>
        )}

        {/* Bridges the gap between a successful invite and the parent's refetch
            landing. Without it the seller's click appears to do nothing until
            they reload the page. Replaced by the real branch above as soon as
            the server reports already_in_room. */}
        {branch !== 'already_in_room' && justInvited && (
          <span className="text-sm text-succession-text-secondary" data-rj-branch="pending_invite">
            Adding to your Meeting Room…
          </span>
        )}

        {/* Branches (a) and (b) share a button; only the label differs, because to
            the seller it is one intent — take the next step with this buyer. */}
        {showInviteButton && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => handleInvite()}
            disabled={inviting || conversationEnded}
            data-rj-branch={branch}
            title={
              branch === 'must_create'
                ? 'Create a Meeting Room and invite this buyer into it.'
                : 'Move this conversation into a private, NDA-gated Meeting Room.'
            }
          >
            {inviting
              ? 'Inviting…'
              : branch === 'must_create'
                ? 'Create Meeting Room'
                : 'Invite to Meeting Room'}
          </Button>
        )}
        {!conversationEnded && canStopMessaging && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowEndConfirm(true)}
            disabled={ending}
            title="Pause this conversation. Neither you nor the buyer can send messages until you reopen it."
          >
            Stop Messaging
          </Button>
        )}
        {conversationEnded && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleReopen}
            disabled={reopening}
            title="Reopen this conversation so you and the buyer can send messages again."
          >
            {reopening ? 'Reopening…' : 'Reopen Conversation'}
          </Button>
        )}
      </div>

      {showEndConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white border rounded-lg max-w-md w-full shadow-xl">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">Stop messaging for now?</h3>
            </div>
            <div className="p-6 space-y-3">
              <p className="text-sm text-gray-900">
                Messaging pauses for <strong>both of you</strong> — neither you nor the buyer can send
                further messages in this conversation. The buyer keeps access to your profile. Only you
                can <strong>reopen</strong> it, from your inbox, at any time.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-succession-border flex justify-end gap-3">
              <Button variant="outline" size="md" onClick={() => setShowEndConfirm(false)}>
                Cancel
              </Button>
              <Button variant="primary" size="md" onClick={handleEnd} disabled={ending}>
                {ending ? 'Stopping…' : 'Yes, stop messaging'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Branch (b) with several rooms. The server refuses with
          `multiple_meeting_rooms` rather than guessing which one the seller meant. */}
      {showRoomPicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white border rounded-lg max-w-md w-full shadow-xl">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">Which Meeting Room?</h3>
            </div>
            <div className="p-6 space-y-2">
              <p className="text-sm text-gray-900">
                You have more than one Meeting Room. Choose where to invite this buyer.
              </p>
              {rooms.map((room, i) => (
                <Button
                  key={room.id}
                  variant="outline"
                  size="md"
                  onClick={() => handleInvite(room.id)}
                  disabled={inviting}
                  data-rj-room-id={room.id}
                >
                  {room.name || `Meeting Room ${i + 1}`}
                </Button>
              ))}
            </div>
            <div className="px-6 py-4 border-t border-succession-border flex justify-end">
              <Button variant="outline" size="md" onClick={() => setShowRoomPicker(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {showNudge && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white border rounded-lg max-w-md w-full shadow-xl">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">Consider a Meeting Room?</h3>
            </div>
            <div className="p-6 space-y-3">
              <p className="text-sm text-gray-900 leading-relaxed">{NDA_NUDGE_COPY}</p>
            </div>
            <div className="px-6 py-4 border-t border-succession-border flex justify-end gap-3">
              <Button variant="outline" size="md" onClick={dismissNudge}>
                Not now
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={() => { dismissNudge(); handleInvite(); }}
                disabled={inviting}
              >
                Invite to Meeting Room
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

RjSellerConversationActions.propTypes = {
  conversationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  conversationEnded: PropTypes.bool,
  ndaNudgeTriggered: PropTypes.bool,
  onNudgeDismissed: PropTypes.func,
  onEnded: PropTypes.func,
  onReopened: PropTypes.func,
  onInvited: PropTypes.func,
  ndaWizardHref: PropTypes.string, // optional: URL template with :deal_id placeholder for post-invite redirect
  // Server-computed branch state (PublicUserType.rj_meeting_room_invite_state).
  // This component must NOT re-derive any of it — two independent client-side
  // gates are what let this feature drift out of sync in the first place.
  conversationType: PropTypes.string,
  inviteState: PropTypes.shape({
    branch: PropTypes.string,
    deal_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    workflow_state: PropTypes.string,
    deals: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    })),
  }),
  buyerUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

RjSellerConversationActions.defaultProps = {
  conversationEnded: false,
  ndaNudgeTriggered: false,
  conversationType: null,
  inviteState: null,
  buyerUserId: null,
};

export default RjSellerConversationActions;
